import { createClient, User, Session } from '@supabase/supabase-js'

// Replace these with your actual Supabase project credentials
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || ''
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || ''

const isSupabaseConfigured = !!supabaseUrl && !!supabaseAnonKey

if (!isSupabaseConfigured) {
  console.warn('Supabase credentials not found. Using localStorage fallback.')
}

// Only create client if credentials are available
export const supabase = isSupabaseConfigured 
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null

// Type definitions for our lesson plans
export interface LessonPlanDB {
  id: string
  title: string
  steps: any[] // JSON array of lesson steps
  created_at: string
  email?: string
  topic: string
}

// Authentication types
export interface AuthState {
  user: User | null
  session: Session | null
  isAdmin: boolean
  isLoading: boolean
}

// Type definitions for cache tables
export interface QuizCacheDB {
  id: string
  topic: string
  questions: any[] // JSON array of quiz questions
  created_at: string
  updated_at: string
}

export interface VideosCacheDB {
  id: string
  topic: string
  videos_data: any[] // JSON array of video data
  created_at: string
  updated_at: string
}

// Database operations
export const lessonPlanService = {
  // Expose supabase client for direct queries
  supabase,

  // Create a new lesson plan
  async create(lessonPlan: Omit<LessonPlanDB, 'created_at'>) {
    if (!supabase) throw new Error('Supabase not configured')
    
    const { data, error } = await supabase
      .from('lesson_plans')
      .insert([lessonPlan])
      .select()
      .single()

    if (error) throw error
    return data
  },

  // Get a lesson plan by ID
  async getById(id: string) {
    if (!supabase) throw new Error('Supabase not configured')
    
    const { data, error } = await supabase
      .from('lesson_plans')
      .select('*')
      .eq('id', id)
      .single()

    if (error) throw error
    return data
  },

  // Get all lesson plans
  async getAll() {
    if (!supabase) throw new Error('Supabase not configured')
    
    const { data, error } = await supabase
      .from('lesson_plans')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) throw error
    return data
  },

  // Check if Supabase is configured
  isConfigured() {
    return isSupabaseConfigured && !!supabase
  }
}

// Quiz cache service with fallback to webhook
export const quizCacheService = {
  // Get quiz from cache or webhook
  async getQuiz(topic: string) {
    const normalizedTopic = topic.toLowerCase().trim()
    
    try {
      // First, try to get from cache
      if (lessonPlanService.isConfigured() && supabase) {
        const { data: cachedQuiz, error } = await supabase
          .from('quiz_cache')
          .select('*')
          .eq('topic', normalizedTopic)
          .single()

        if (!error && cachedQuiz) {
          console.log('Quiz loaded from cache:', normalizedTopic)
          return cachedQuiz.questions
        }
      }

      // If not in cache, fetch from webhook
      console.log('Quiz not in cache, fetching from webhook:', normalizedTopic)
      const response = await fetch(process.env.REACT_APP_WEBHOOK_QUIZ_URL!, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_WEBHOOK_JWT_TOKEN}`
        },
        body: JSON.stringify({ 
          topic: topic,
          source: 'cache-service'
        })
      })

      if (!response.ok) {
        throw new Error('Failed to fetch quiz from webhook')
      }

      const data = await response.json()
      let questions: any[] = []
      
      // Parse the response format
      if (Array.isArray(data) && data[0]?.output?.questions) {
        const rawQuestions = data[0].output.questions
        questions = rawQuestions.map((q: any, index: number) => ({
          id: index + 1,
          question: q.question,
          choices: Object.values(q.options) as string[],
          correctAnswer: q.options[q.correctAnswer]
        }))
      } else if (data.questions && Array.isArray(data.questions)) {
        questions = data.questions
      } else {
        throw new Error('Invalid quiz response format')
      }

      // Cache the result for future use
      if (lessonPlanService.isConfigured() && supabase) {
        try {
          await supabase
            .from('quiz_cache')
            .upsert({
              topic: normalizedTopic,
              questions: questions,
              updated_at: new Date().toISOString()
            })
          console.log('Quiz cached successfully:', normalizedTopic)
        } catch (cacheError) {
          console.warn('Failed to cache quiz:', cacheError)
          // Don't throw - just log the warning since we have the data
        }
      }

      return questions
    } catch (error) {
      console.error('Error fetching quiz:', error)
      throw error
    }
  },

  // Clear cache for a specific topic (admin function)
  async clearCache(topic: string) {
    if (!lessonPlanService.isConfigured() || !supabase) return
    
    const { error } = await supabase
      .from('quiz_cache')
      .delete()
      .eq('topic', topic.toLowerCase().trim())
      
    if (error) throw error
  }
}

// Videos cache service with fallback to webhook
export const videosCacheService = {
  // Get videos from cache or webhook
  async getVideos(topic: string, mainTopic?: string) {
    const normalizedTopic = topic.toLowerCase().trim()
    
    try {
      // First, try to get from cache
      if (lessonPlanService.isConfigured() && supabase) {
        const { data: cachedVideos, error } = await supabase
          .from('videos_cache')
          .select('*')
          .eq('topic', normalizedTopic)
          .single()

        if (!error && cachedVideos) {
          console.log('Videos loaded from cache:', normalizedTopic)
          return cachedVideos.videos_data
        }
      }

      // If not in cache, fetch from webhook
      console.log('Videos not in cache, fetching from webhook:', normalizedTopic)
      const response = await fetch(process.env.REACT_APP_WEBHOOK_VIDEOS_URL!, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_WEBHOOK_JWT_TOKEN}`
        },
        body: JSON.stringify({
          mainTopic: mainTopic || topic,
          subTopic: topic,
          timestamp: new Date().toISOString(),
          source: 'cache-service'
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const videosData = await response.json()

      // Cache the result for future use
      if (lessonPlanService.isConfigured() && supabase) {
        try {
          await supabase
            .from('videos_cache')
            .upsert({
              topic: normalizedTopic,
              videos_data: videosData,
              updated_at: new Date().toISOString()
            })
          console.log('Videos cached successfully:', normalizedTopic)
        } catch (cacheError) {
          console.warn('Failed to cache videos:', cacheError)
          // Don't throw - just log the warning since we have the data
        }
      }

      return videosData
    } catch (error) {
      console.error('Error fetching videos:', error)
      throw error
    }
  },

  // Clear cache for a specific topic (admin function)  
  async clearCache(topic: string) {
    if (!lessonPlanService.isConfigured() || !supabase) return
    
    const { error } = await supabase
      .from('videos_cache')
      .delete()
      .eq('topic', topic.toLowerCase().trim())
      
    if (error) throw error
  }
}

// Project voting service
export const projectVotingService = {
  // Get or create a voter ID for this browser
  getVoterId() {
    let voterId = localStorage.getItem('voterId');
    if (!voterId) {
      voterId = crypto.randomUUID();
      localStorage.setItem('voterId', voterId);
    }
    return voterId;
  },

  // Get all votes for the current user
  async getUserVotes() {
    if (!lessonPlanService.isConfigured() || !supabase) {
      // Fallback to localStorage
      const stored = localStorage.getItem('userProjectVotes');
      return stored ? JSON.parse(stored) : [];
    }

    try {
      const voterId = this.getVoterId();
      const { data, error } = await supabase
        .from('project_votes')
        .select('project_id')
        .eq('voter_id', voterId);

      if (error) throw error;
      return data?.map(v => v.project_id) || [];
    } catch (error) {
      console.error('Error fetching user votes:', error);
      // Fallback to localStorage
      const stored = localStorage.getItem('userProjectVotes');
      return stored ? JSON.parse(stored) : [];
    }
  },

  // Get vote counts for all projects
  async getVoteCounts() {
    if (!lessonPlanService.isConfigured() || !supabase) {
      // Fallback to localStorage
      const stored = localStorage.getItem('projectVotes');
      return stored ? JSON.parse(stored) : {};
    }

    try {
      const { data, error } = await supabase
        .from('project_vote_counts')
        .select('*');

      if (error) throw error;
      
      // Convert array to object
      const counts: Record<string, number> = {};
      data?.forEach(item => {
        counts[item.project_id] = item.vote_count;
      });
      return counts;
    } catch (error) {
      console.error('Error fetching vote counts:', error);
      // Fallback to localStorage
      const stored = localStorage.getItem('projectVotes');
      return stored ? JSON.parse(stored) : {};
    }
  },

  // Toggle vote for a project
  async toggleVote(projectId: string) {
    const voterId = this.getVoterId();

    if (!lessonPlanService.isConfigured() || !supabase) {
      // Fallback to localStorage
      const userVotes = JSON.parse(localStorage.getItem('userProjectVotes') || '[]');
      const projectVotes = JSON.parse(localStorage.getItem('projectVotes') || '{}');
      
      if (userVotes.includes(projectId)) {
        // Remove vote
        const newUserVotes = userVotes.filter((id: string) => id !== projectId);
        projectVotes[projectId] = Math.max(0, (projectVotes[projectId] || 1) - 1);
        if (projectVotes[projectId] === 0) delete projectVotes[projectId];
        
        localStorage.setItem('userProjectVotes', JSON.stringify(newUserVotes));
        localStorage.setItem('projectVotes', JSON.stringify(projectVotes));
        return { voted: false, userVotes: newUserVotes, voteCounts: projectVotes };
      } else {
        // Add vote
        const newUserVotes = [...userVotes, projectId];
        projectVotes[projectId] = (projectVotes[projectId] || 0) + 1;
        
        localStorage.setItem('userProjectVotes', JSON.stringify(newUserVotes));
        localStorage.setItem('projectVotes', JSON.stringify(projectVotes));
        return { voted: true, userVotes: newUserVotes, voteCounts: projectVotes };
      }
    }

    try {
      // Check if already voted
      const { data: existingVote, error: checkError } = await supabase
        .from('project_votes')
        .select('id')
        .eq('project_id', projectId)
        .eq('voter_id', voterId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = no rows found
        throw checkError;
      }

      if (existingVote) {
        // Remove vote
        const { error: deleteError } = await supabase
          .from('project_votes')
          .delete()
          .eq('project_id', projectId)
          .eq('voter_id', voterId);

        if (deleteError) throw deleteError;

        // Get updated data
        const [userVotes, voteCounts] = await Promise.all([
          this.getUserVotes(),
          this.getVoteCounts()
        ]);

        return { voted: false, userVotes, voteCounts };
      } else {
        // Add vote
        const { error: insertError } = await supabase
          .from('project_votes')
          .insert([{ project_id: projectId, voter_id: voterId }]);

        if (insertError) throw insertError;

        // Get updated data
        const [userVotes, voteCounts] = await Promise.all([
          this.getUserVotes(),
          this.getVoteCounts()
        ]);

        return { voted: true, userVotes, voteCounts };
      }
    } catch (error) {
      console.error('Error toggling vote:', error);
      throw error;
    }
  }
}

// Audio storage service
export const audioStorageService = {
  // Store audio URL and metadata for a lesson step
  async storeAudio(lessonId: string, stepTitle: string, audioUrl: string, audioData?: Blob) {
    console.log('🎵 [AUDIO DEBUG] Starting storeAudio:', {
      lessonId,
      stepTitle,
      audioUrl: audioUrl?.substring(0, 100) + '...',
      hasAudioData: !!audioData,
      audioBlobSize: audioData?.size
    });

    if (!lessonPlanService.isConfigured() || !supabase) {
      console.log('🎵 [AUDIO DEBUG] Supabase not configured, using localStorage fallback');
      const audioKey = `audio_${lessonId}_${stepTitle.replace(/\s+/g, '_').toLowerCase()}`;
      console.log('🎵 [AUDIO DEBUG] Storing to localStorage with key:', audioKey);
      localStorage.setItem(audioKey, audioUrl);
      return { audioKey, url: audioUrl };
    }

    try {
      console.log('🎵 [AUDIO DEBUG] Supabase configured, proceeding with upload...');

      // If we have audio data (Blob), upload to Supabase storage
      let storageUrl = audioUrl;
      if (audioData) {
        console.log('🎵 [AUDIO DEBUG] AudioData provided, uploading to Supabase Storage...');
        console.log('🎵 [AUDIO DEBUG] Audio blob details:', {
          size: audioData.size,
          type: audioData.type,
          sizeInMB: (audioData.size / 1024 / 1024).toFixed(2) + 'MB'
        });

        const fileName = `${lessonId}/${stepTitle.replace(/\s+/g, '_').toLowerCase()}_${Date.now()}.mp3`;
        console.log('🎵 [AUDIO DEBUG] Generated filename:', fileName);

        console.log('🎵 [AUDIO DEBUG] Starting Supabase storage upload...');
        const { error: uploadError } = await supabase.storage
          .from('lesson-audio')
          .upload(fileName, audioData, {
            contentType: 'audio/mpeg',
            upsert: false
          });

        if (uploadError) {
          console.error('🎵 [AUDIO DEBUG] ❌ Upload failed:', uploadError);
          console.error('🎵 [AUDIO DEBUG] Upload error details:', {
            message: uploadError.message,
            name: uploadError.name
          });
          throw uploadError;
        }

        console.log('🎵 [AUDIO DEBUG] ✅ Upload successful! Getting public URL...');

        // Get public URL
        const { data: urlData } = supabase.storage
          .from('lesson-audio')
          .getPublicUrl(fileName);

        console.log('🎵 [AUDIO DEBUG] Generated public URL:', urlData.publicUrl);
        storageUrl = urlData.publicUrl;
      } else {
        console.log('🎵 [AUDIO DEBUG] No audioData provided, using original URL:', audioUrl);
      }

      // Store metadata in database
      console.log('🎵 [AUDIO DEBUG] Storing metadata in lesson_audio table...');
      console.log('🎵 [AUDIO DEBUG] Metadata to store:', {
        lesson_id: lessonId,
        step_title: stepTitle,
        audio_url: storageUrl
      });

      const { data, error } = await supabase
        .from('lesson_audio')
        .upsert({
          lesson_id: lessonId,
          step_title: stepTitle,
          audio_url: storageUrl,
          created_at: new Date().toISOString()
        }, {
          onConflict: 'lesson_id,step_title'
        })
        .select()
        .single();

      if (error) {
        console.error('🎵 [AUDIO DEBUG] ❌ Database storage failed:', error);
        throw error;
      }

      console.log('🎵 [AUDIO DEBUG] ✅ Successfully stored audio metadata:', data);
      return { url: storageUrl, data };
    } catch (error) {
      console.error('🎵 [AUDIO DEBUG] ❌ Full storage process failed:', error);

      // Type-safe error logging
      if (error instanceof Error) {
        console.error('🎵 [AUDIO DEBUG] Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
      } else {
        console.error('🎵 [AUDIO DEBUG] Unknown error type:', typeof error);
      }

      // Fallback to localStorage
      console.log('🎵 [AUDIO DEBUG] Falling back to localStorage...');
      const audioKey = `audio_${lessonId}_${stepTitle.replace(/\s+/g, '_').toLowerCase()}`;
      console.log('🎵 [AUDIO DEBUG] localStorage key:', audioKey);
      localStorage.setItem(audioKey, audioUrl);
      return { audioKey, url: audioUrl };
    }
  },

  // Get audio URL for a lesson step
  async getAudio(lessonId: string, stepTitle: string) {
    if (!lessonPlanService.isConfigured() || !supabase) {
      // Fallback to localStorage
      const audioKey = `audio_${lessonId}_${stepTitle.replace(/\s+/g, '_').toLowerCase()}`;
      const url = localStorage.getItem(audioKey);
      return url ? { url } : null;
    }

    try {
      const { data, error } = await supabase
        .from('lesson_audio')
        .select('*')
        .eq('lesson_id', lessonId)
        .eq('step_title', stepTitle)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching audio:', error);
      // Fallback to localStorage
      const audioKey = `audio_${lessonId}_${stepTitle.replace(/\s+/g, '_').toLowerCase()}`;
      const url = localStorage.getItem(audioKey);
      return url ? { url } : null;
    }
  },

  // Get all audio for a lesson
  async getLessonAudio(lessonId: string) {
    if (!lessonPlanService.isConfigured() || !supabase) {
      // Fallback to localStorage - scan for audio keys
      console.log('Scanning localStorage for audio keys with lesson ID:', lessonId);
      const audioMap: Record<string, string> = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        console.log('Checking localStorage key:', key);
        if (key?.startsWith(`audio_${lessonId}_`)) {
          const stepTitle = key.replace(`audio_${lessonId}_`, '').replace(/_/g, ' ');
          const url = localStorage.getItem(key);
          console.log('Found audio key:', key, 'stepTitle:', stepTitle, 'url:', url);
          if (url) audioMap[stepTitle] = url;
        }
      }
      console.log('Final audioMap from localStorage:', audioMap);
      return audioMap;
    }

    try {
      const { data, error } = await supabase
        .from('lesson_audio')
        .select('*')
        .eq('lesson_id', lessonId);

      if (error) throw error;

      // Convert to map
      const audioMap: Record<string, string> = {};
      data?.forEach(item => {
        audioMap[item.step_title] = item.audio_url;
      });
      
      return audioMap;
    } catch (error) {
      console.error('Error fetching lesson audio:', error);
      return {};
    }
  }
}

// Authentication service
export const authService = {
  // Admin emails that have full access
  ADMIN_EMAILS: [
    // Add admin emails here
    'info@builderthinking.com', 'vijvin.brainstorm@gmail.com'
    // You can add more admin emails as needed
  ],

  // Sign up new admin user
  async signUp(email: string, password: string, role: 'admin' | 'builder' = 'builder') {
    if (!supabase) throw new Error('Supabase not configured')
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          role: role
        }
      }
    })

    if (error) throw error
    return data
  },

  // Sign in user
  async signIn(email: string, password: string) {
    if (!supabase) throw new Error('Supabase not configured')
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })

    if (error) throw error
    return data
  },

  // Sign out user
  async signOut() {
    if (!supabase) throw new Error('Supabase not configured')
    
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  },

  // Get current user session
  async getSession() {
    if (!supabase) return null
    
    const { data: { session } } = await supabase.auth.getSession()
    return session
  },

  // Check if current user is admin
  isAdmin(email: string | undefined): boolean {
    if (!email) return false
    return this.ADMIN_EMAILS.includes(email.toLowerCase())
  },

  // Listen to auth state changes
  onAuthStateChange(callback: (event: string, session: Session | null) => void) {
    if (!supabase) return { data: { subscription: { unsubscribe: () => {} } } }
    
    return supabase.auth.onAuthStateChange(callback)
  }
}
